import { getSupabase } from "../auth/supabaseClient";
import type { LobbyData, LobbyPlayer, LobbySettings, LobbyChatMessage, MatchResult, MatchSummary } from "./types";
import {
  isProvisionalRankedPlayer,
  percentileForRank,
  PROVISIONAL_RANKED_GAMES,
  rankedTierForRating,
  type LobbyQueueType,
  type RankedLeaderboardEntry,
  type RankedPlayerSummary,
  type RankedRankContext
} from "./ranked";

export type VersusDbErrorKind = "network" | "schema" | "backend";

export interface VersusDbError {
  kind: VersusDbErrorKind;
  message: string;
  rawMessage: string;
  code: string | null;
}

export interface VersusDbMutationResult {
  ok: boolean;
  error: VersusDbError | null;
}

export interface FetchLobbyResult {
  lobby: LobbyData | null;
  error: VersusDbError | null;
}

export interface FetchOpenLobbiesResult {
  lobbies: LobbyData[];
  error: VersusDbError | null;
}

interface SupabaseErrorLike {
  code?: string | null;
  details?: string | null;
  hint?: string | null;
  message?: string | null;
}

const LEGACY_MATCH_SCHEMA_COLUMNS = [
  "queue_type",
  "player1_account_level_before",
  "player2_account_level_before",
  "player1_rating_before",
  "player2_rating_before",
  "player1_rating_after",
  "player2_rating_after",
  "ranked_applied_at"
] as const;

function isNetworkLikeMessage(message: string): boolean {
  const upper = message.toUpperCase();
  return (
    upper.includes("NETWORK")
    || upper.includes("OFFLINE")
    || upper.includes("FETCH")
    || upper.includes("LOAD FAILED")
    || upper.includes("TIMEOUT")
    || upper.includes("TIMED OUT")
    || upper.includes("ABORT")
    || upper.includes("ERR_INTERNET_DISCONNECTED")
    || upper.includes("NETWORK REQUEST FAILED")
  );
}

function classifyVersusDbError(rawMessage: string): VersusDbErrorKind {
  const upper = rawMessage.toUpperCase();
  if (upper.includes("QUEUE_TYPE")) {
    return "schema";
  }
  if (isNetworkLikeMessage(rawMessage)) {
    return "network";
  }
  return "backend";
}

function normalizeSupabaseError(input: unknown): SupabaseErrorLike {
  if (input instanceof Error) {
    return { message: input.message };
  }
  if (typeof input === "string") {
    return { message: input };
  }
  if (typeof input === "object" && input != null) {
    const maybeError = input as SupabaseErrorLike;
    return {
      code: maybeError.code ?? null,
      details: maybeError.details ?? null,
      hint: maybeError.hint ?? null,
      message: maybeError.message ?? null
    };
  }
  return {};
}

function buildRawErrorMessage(error: SupabaseErrorLike): string {
  return [
    error.message?.trim(),
    error.details?.trim(),
    error.hint?.trim()
  ]
    .filter((part): part is string => part != null && part.length > 0)
    .join(" | ") || "Unknown backend error";
}

function isMissingSchemaColumnError(input: unknown, columns: readonly string[]): boolean {
  const rawMessage = buildRawErrorMessage(normalizeSupabaseError(input)).toUpperCase();
  const mentionsMissingColumn =
    rawMessage.includes("COLUMN")
    || rawMessage.includes("SCHEMA CACHE")
    || rawMessage.includes("DOES NOT EXIST");
  if (!mentionsMissingColumn) {
    return false;
  }
  return columns.some((column) => rawMessage.includes(column.toUpperCase()));
}

function toVersusDbError(input: unknown): VersusDbError {
  const error = normalizeSupabaseError(input);
  const rawMessage = buildRawErrorMessage(error);
  return {
    kind: classifyVersusDbError(rawMessage),
    message: error.message?.trim() || rawMessage,
    rawMessage,
    code: error.code?.trim() || null
  };
}

// ── Profile versus stats ────────────────────────────────────────────────────

export interface VersusProfile {
  versusWins: number;
  versusLosses: number;
  versusPlayed: number;
  versusElo: number;
  rankedWins: number;
  rankedLosses: number;
  rankedGames: number;
  rankedDisconnects: number;
  rankedPeakRating: number;
}

export async function fetchVersusProfile(userId: string): Promise<VersusProfile | null> {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("profiles")
      .select("versus_wins, versus_losses, versus_played, versus_elo, ranked_wins, ranked_losses, ranked_games, ranked_disconnects, ranked_peak_rating")
      .eq("id", userId)
      .single();
    if (error || data == null) return null;
    return {
      versusWins: data.versus_wins ?? 0,
      versusLosses: data.versus_losses ?? 0,
      versusPlayed: data.versus_played ?? 0,
      versusElo: data.versus_elo ?? 0,
      rankedWins: data.ranked_wins ?? data.versus_wins ?? 0,
      rankedLosses: data.ranked_losses ?? data.versus_losses ?? 0,
      rankedGames: data.ranked_games ?? data.versus_played ?? 0,
      rankedDisconnects: data.ranked_disconnects ?? 0,
      rankedPeakRating: data.ranked_peak_rating ?? data.versus_elo ?? 0
    };
  } catch {
    return null;
  }
}

interface RankedProfileRow {
  id: string;
  display_name: string | null;
  versus_elo: number | null;
  ranked_wins: number | null;
  ranked_losses: number | null;
  ranked_games: number | null;
  ranked_disconnects: number | null;
  ranked_peak_rating: number | null;
  level: number | null;
}

function normalizeRankedRow(row: RankedProfileRow) {
  const rating = Math.max(0, row.versus_elo ?? 0)
  const rankedWins = Math.max(0, row.ranked_wins ?? 0)
  const rankedLosses = Math.max(0, row.ranked_losses ?? 0)
  const rankedGames = Math.max(0, row.ranked_games ?? 0)
  const rankedDisconnects = Math.max(0, row.ranked_disconnects ?? 0)
  const rankedPeakRating = Math.max(rating, row.ranked_peak_rating ?? rating)
  const level = Math.max(1, row.level ?? 1)
  return {
    userId: row.id,
    displayName: row.display_name ?? "PLAYER",
    rating,
    rankedWins,
    rankedLosses,
    rankedGames,
    rankedDisconnects,
    rankedPeakRating,
    level,
    provisional: isProvisionalRankedPlayer(rankedGames),
    tier: rankedTierForRating(rating)
  }
}

function toRankedLeaderboardEntry(row: RankedProfileRow, rank: number): RankedLeaderboardEntry {
  const normalized = normalizeRankedRow(row)
  return {
    ...normalized,
    rank
  }
}

function toRankedPlayerSummary(
  row: RankedProfileRow,
  rank: number | null,
  totalEligiblePlayers: number
): RankedPlayerSummary {
  const normalized = normalizeRankedRow(row)
  return {
    ...normalized,
    rank,
    percentile: percentileForRank(rank, totalEligiblePlayers)
  }
}

function rankedOrderQuery(query: any): any {
  return query
    .order("versus_elo", { ascending: false })
    .order("ranked_peak_rating", { ascending: false })
    .order("ranked_wins", { ascending: false })
    .order("id", { ascending: true })
}

export async function fetchRankedLeaderboard(limit = 100): Promise<RankedLeaderboardEntry[]> {
  try {
    const supabase = getSupabase()
    const query = rankedOrderQuery(
      supabase
        .from("profiles")
        .select("id, display_name, versus_elo, ranked_wins, ranked_losses, ranked_games, ranked_disconnects, ranked_peak_rating, level")
        .gte("ranked_games", PROVISIONAL_RANKED_GAMES)
    )
    const { data, error } = await query.limit(limit)
    if (error != null || data == null) {
      return []
    }
    return (data as RankedProfileRow[]).map((row, index) => toRankedLeaderboardEntry(row, index + 1))
  } catch {
    return []
  }
}

export async function fetchRankedContext(
  userId: string,
  windowSize = 1
): Promise<RankedRankContext | null> {
  try {
    const supabase = getSupabase()
    const { data: playerRow, error: playerError } = await supabase
      .from("profiles")
      .select("id, display_name, versus_elo, ranked_wins, ranked_losses, ranked_games, ranked_disconnects, ranked_peak_rating, level")
      .eq("id", userId)
      .maybeSingle()
    if (playerError != null || playerRow == null) {
      return null
    }

    const { error: countError } = await supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .gte("ranked_games", PROVISIONAL_RANKED_GAMES)
    if (countError != null) {
      return null
    }

    const normalizedPlayer = normalizeRankedRow(playerRow as RankedProfileRow)

    // Calculate global rank among ALL players with any ranked games
    const aboveFilter = [
      `versus_elo.gt.${normalizedPlayer.rating}`,
      `and(versus_elo.eq.${normalizedPlayer.rating},ranked_peak_rating.gt.${normalizedPlayer.rankedPeakRating})`,
      `and(versus_elo.eq.${normalizedPlayer.rating},ranked_peak_rating.eq.${normalizedPlayer.rankedPeakRating},ranked_wins.gt.${normalizedPlayer.rankedWins})`,
      `and(versus_elo.eq.${normalizedPlayer.rating},ranked_peak_rating.eq.${normalizedPlayer.rankedPeakRating},ranked_wins.eq.${normalizedPlayer.rankedWins},id.lt.${normalizedPlayer.userId})`
    ].join(",")

    const { count: aboveCount, error: aboveError } = await supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .gt("ranked_games", 0)
      .or(aboveFilter)
    if (aboveError != null) {
      return null
    }

    // Total players who have played any ranked game
    const { count: totalRankedPlayers, error: totalRankedError } = await supabase
      .from("profiles")
      .select("id", { count: "exact", head: true })
      .gt("ranked_games", 0)
    if (totalRankedError != null) {
      return null
    }

    const rank = (aboveCount ?? 0) + 1
    const globalTotal = totalRankedPlayers ?? 0
    const clampedWindow = Math.max(0, Math.floor(windowSize))
    const start = Math.max(0, rank - 1 - clampedWindow)
    const end = start + (clampedWindow * 2)
    const { data: neighborRows, error: neighborError } = await rankedOrderQuery(
      supabase
        .from("profiles")
        .select("id, display_name, versus_elo, ranked_wins, ranked_losses, ranked_games, ranked_disconnects, ranked_peak_rating, level")
        .gte("ranked_games", PROVISIONAL_RANKED_GAMES)
    ).range(start, end)
    if (neighborError != null || neighborRows == null) {
      return null
    }

    return {
      player: toRankedPlayerSummary(playerRow as RankedProfileRow, rank, globalTotal),
      totalEligiblePlayers: globalTotal,
      neighbors: (neighborRows as RankedProfileRow[]).map((row, index) => toRankedLeaderboardEntry(row, start + index + 1))
    }
  } catch {
    return null
  }
}

// ── Lobby CRUD ──────────────────────────────────────────────────────────────

export async function createLobby(
  lobbyId: string,
  hostId: string,
  settings: LobbySettings,
  queueType: LobbyQueueType = "custom"
): Promise<VersusDbMutationResult> {
  try {
    const supabase = getSupabase();
    const insertLobby = async (includeQueueType: boolean) => {
      const payload: Record<string, unknown> = {
        id: lobbyId,
        host_id: hostId,
        settings
      };
      if (includeQueueType) {
        payload.queue_type = queueType;
      }
      return supabase
        .from("lobbies")
        .insert(payload);
    };

    let { error: lobbyError } = await insertLobby(true);
    if (lobbyError != null && isMissingSchemaColumnError(lobbyError, ["queue_type"])) {
      ({ error: lobbyError } = await insertLobby(false));
    }
    if (lobbyError) return { ok: false, error: toVersusDbError(lobbyError) };

    const { error: memberError } = await supabase
      .from("lobby_members")
      .insert({ lobby_id: lobbyId, user_id: hostId });
    if (memberError) return { ok: false, error: toVersusDbError(memberError) };

    return { ok: true, error: null };
  } catch (error) {
    return { ok: false, error: toVersusDbError(error) };
  }
}

function buildLobbySelect(includeQueueType: boolean, includeTimestamps = false): string {
  const queueTypeColumn = includeQueueType ? ", queue_type" : "";
  const timestampColumns = includeTimestamps ? ", created_at, updated_at" : "";
  return `
    id, host_id${queueTypeColumn}, state, settings, match_id${timestampColumns},
    lobby_members (
      user_id,
      ready,
      profiles ( id, display_name, avatar_url, versus_elo, equipped_character, level )
    )
  `;
}

async function runFetchOpenLobbiesQuery(
  queueType: LobbyQueueType,
  includeQueueType: boolean
): Promise<{ data: unknown[] | null; error: unknown }> {
  const supabase = getSupabase();
  const freshnessIso = new Date(Date.now() - 20 * 60 * 1000).toISOString();
  let query = supabase
    .from("lobbies")
    .select(buildLobbySelect(includeQueueType, true));
  if (includeQueueType) {
    query = query.eq("queue_type", queueType);
  }
  return query
    .eq("state", "WAITING")
    .gte("updated_at", freshnessIso)
    .order("updated_at", { ascending: false })
    .limit(20);
}

export async function fetchOpenLobbies(
  queueType: LobbyQueueType = "custom"
): Promise<FetchOpenLobbiesResult> {
  try {
    let { data, error } = await runFetchOpenLobbiesQuery(queueType, true);
    if (error != null && isMissingSchemaColumnError(error, ["queue_type"])) {
      ({ data, error } = await runFetchOpenLobbiesQuery(queueType, false));
    }
    if (error) {
      return { lobbies: [], error: toVersusDbError(error) };
    }
    return {
      lobbies: ((data ?? []) as Record<string, unknown>[])
        .map((row) => rowToLobbyData(row, queueType))
        .filter((lobby) => lobby.players.length > 0 && lobby.players.length < 2),
      error: null
    };
  } catch (error) {
    return { lobbies: [], error: toVersusDbError(error) };
  }
}

/**
 * Find the most recent WAITING lobby hosted by a specific user.
 * Used for friend battles — Player 2 looks up Player 1's lobby by host_id.
 */
export async function fetchLobbyByHost(hostUserId: string, queueType: LobbyQueueType = "custom"): Promise<LobbyData | null> {
  try {
    const supabase = getSupabase();
    const freshnessIso = new Date(Date.now() - 20 * 60 * 1000).toISOString();
    const queryLobbyByHost = (includeQueueType: boolean) => {
      let query = supabase
        .from("lobbies")
        .select(buildLobbySelect(includeQueueType))
        .eq("host_id", hostUserId);
      if (includeQueueType) {
        query = query.eq("queue_type", queueType);
      }
      return query
        .eq("state", "WAITING")
        .gte("updated_at", freshnessIso)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
    };

    let { data, error } = await queryLobbyByHost(true);
    if (error != null && isMissingSchemaColumnError(error, ["queue_type"])) {
      ({ data, error } = await queryLobbyByHost(false));
    }
    if (error || data == null) return null;
    const lobby = rowToLobbyData(data as unknown as Record<string, unknown>, queueType);
    // Must have the host as a member and have room
    if (lobby.players.length === 0 || lobby.players.length >= 2) return null;
    return lobby;
  } catch {
    return null;
  }
}

export async function fetchLobby(
  lobbyId: string,
  fallbackQueueType: LobbyQueueType = "custom"
): Promise<FetchLobbyResult> {
  try {
    const supabase = getSupabase();
    const queryLobby = (includeQueueType: boolean) => supabase
      .from("lobbies")
      .select(buildLobbySelect(includeQueueType))
      .eq("id", lobbyId)
      .maybeSingle();

    let { data, error } = await queryLobby(true);
    if (error != null && isMissingSchemaColumnError(error, ["queue_type"])) {
      ({ data, error } = await queryLobby(false));
    }
    if (error) {
      return { lobby: null, error: toVersusDbError(error) };
    }
    return {
      lobby: data == null ? null : rowToLobbyData(data as unknown as Record<string, unknown>, fallbackQueueType),
      error: null
    };
  } catch (error) {
    return { lobby: null, error: toVersusDbError(error) };
  }
}

function rowToLobbyData(
  row: Record<string, unknown>,
  fallbackQueueType: LobbyQueueType = "custom"
): LobbyData {
  const members = (row.lobby_members ?? []) as Array<{
    user_id: string;
    ready: boolean;
    profiles: {
      id: string;
      display_name: string;
      avatar_url: string | null;
      versus_elo: number;
      equipped_character: string | null;
      level: number | null;
    } | null;
  }>;
  const players: LobbyPlayer[] = members.map((m) => ({
    userId: m.user_id,
    displayName: m.profiles?.display_name ?? "PLAYER",
    avatarUrl: m.profiles?.avatar_url ?? null,
    ready: m.ready,
    elo: m.profiles?.versus_elo ?? 0,
    equippedSkin: m.profiles?.equipped_character ?? "beast_starter",
    level: Math.max(1, m.profiles?.level ?? 1)
  }));
  return {
    id: row.id as string,
    hostId: row.host_id as string,
    queueType: (row.queue_type as LobbyQueueType | null) ?? fallbackQueueType,
    state: row.state as LobbyData["state"],
    settings: row.settings as LobbySettings,
    matchId: (row.match_id as string | null) ?? null,
    players
  };
}

export async function joinLobby(lobbyId: string, userId: string): Promise<VersusDbMutationResult> {
  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("lobby_members")
      .insert({ lobby_id: lobbyId, user_id: userId });
    if (error != null) {
      return { ok: false, error: toVersusDbError(error) };
    }
    return { ok: true, error: null };
  } catch (error) {
    return { ok: false, error: toVersusDbError(error) };
  }
}

export async function leaveLobby(lobbyId: string, userId: string): Promise<VersusDbMutationResult> {
  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("lobby_members")
      .delete()
      .eq("lobby_id", lobbyId)
      .eq("user_id", userId);
    if (error != null) {
      return { ok: false, error: toVersusDbError(error) };
    }
    return { ok: true, error: null };
  } catch (error) {
    return { ok: false, error: toVersusDbError(error) };
  }
}

export async function setReady(
  lobbyId: string,
  userId: string,
  ready: boolean
): Promise<VersusDbMutationResult> {
  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("lobby_members")
      .update({ ready })
      .eq("lobby_id", lobbyId)
      .eq("user_id", userId);
    if (error != null) {
      return { ok: false, error: toVersusDbError(error) };
    }
    return { ok: true, error: null };
  } catch (error) {
    return { ok: false, error: toVersusDbError(error) };
  }
}

export async function updateLobbyState(
  lobbyId: string,
  state: LobbyData["state"],
  matchId?: string
): Promise<VersusDbMutationResult> {
  try {
    const supabase = getSupabase();
    const updates: Record<string, unknown> = { state, updated_at: new Date().toISOString() };
    if (matchId != null) {
      updates.match_id = matchId;
    }
    const { error } = await supabase
      .from("lobbies")
      .update(updates)
      .eq("id", lobbyId);
    if (error != null) {
      return { ok: false, error: toVersusDbError(error) };
    }
    return { ok: true, error: null };
  } catch (error) {
    return { ok: false, error: toVersusDbError(error) };
  }
}

export async function closeLobby(lobbyId: string): Promise<VersusDbMutationResult> {
  return updateLobbyState(lobbyId, "CLOSED");
}

// ── Lobby chat ──────────────────────────────────────────────────────────────

export async function fetchLobbyChat(lobbyId: string, limit = 50): Promise<LobbyChatMessage[]> {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("lobby_chat")
      .select("id, user_id, display_name, message, created_at")
      .eq("lobby_id", lobbyId)
      .order("created_at", { ascending: true })
      .limit(limit);
    if (error || data == null) return [];
    return data.map((row) => ({
      id: row.id,
      userId: row.user_id,
      displayName: row.display_name,
      message: row.message,
      createdAt: new Date(row.created_at).getTime()
    }));
  } catch {
    return [];
  }
}

export async function sendChatMessage(
  lobbyId: string,
  userId: string,
  displayName: string,
  message: string
): Promise<boolean> {
  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("lobby_chat")
      .insert({ lobby_id: lobbyId, user_id: userId, display_name: displayName, message });
    return error == null;
  } catch {
    return false;
  }
}

// ── Match CRUD ──────────────────────────────────────────────────────────────

export interface CreateMatchInput {
  matchId: string;
  lobbyId: string | null;
  player1Id: string;
  player2Id: string;
  seed: number;
  settings: LobbySettings;
  queueType: LobbyQueueType;
  player1AccountLevelBefore: number;
  player2AccountLevelBefore: number;
  player1RatingBefore: number;
  player2RatingBefore: number;
}

export async function createMatch(input: CreateMatchInput): Promise<boolean> {
  try {
    const supabase = getSupabase();
    const insertMatch = async (includeRankedSnapshotColumns: boolean) => {
      const payload: Record<string, unknown> = {
        id: input.matchId,
        lobby_id: input.lobbyId,
        player1_id: input.player1Id,
        player2_id: input.player2Id,
        seed: input.seed,
        settings: input.settings
      };
      if (includeRankedSnapshotColumns) {
        payload.queue_type = input.queueType;
        payload.player1_account_level_before = Math.max(1, Math.floor(input.player1AccountLevelBefore));
        payload.player2_account_level_before = Math.max(1, Math.floor(input.player2AccountLevelBefore));
        payload.player1_rating_before = Math.max(0, Math.floor(input.player1RatingBefore));
        payload.player2_rating_before = Math.max(0, Math.floor(input.player2RatingBefore));
      }
      return supabase
        .from("matches")
        .insert(payload);
    };

    let { error } = await insertMatch(true);
    if (error != null && isMissingSchemaColumnError(error, LEGACY_MATCH_SCHEMA_COLUMNS)) {
      ({ error } = await insertMatch(false));
    }
    return error == null;
  } catch {
    return false;
  }
}

/**
 * Finalize a match with results. Figures out player1/player2 ordering
 * from the match row itself, so the caller just passes {playerId, score, level}
 * for each participant. Ranked Elo is applied later by the validation trigger
 * after both client summaries agree on the result.
 */
export async function finalizeMatch(
  matchId: string,
  winnerId: string | null,
  result: MatchResult,
  localPlayerId: string,
  localScore: number,
  localLevel: number,
  _remotePlayerId: string,
  remoteScore: number,
  remoteLevel: number,
  durationSec: number
): Promise<boolean> {
  try {
    const supabase = getSupabase();

    // Fetch the match row to determine player1/player2 ordering
    const { data: row, error: fetchErr } = await supabase
      .from("matches")
      .select("player1_id, player2_id")
      .eq("id", matchId)
      .single();
    if (fetchErr || row == null) return false;

    const isLocalP1 = row.player1_id === localPlayerId;
    const p1Score = isLocalP1 ? localScore : remoteScore;
    const p2Score = isLocalP1 ? remoteScore : localScore;
    const p1Level = isLocalP1 ? localLevel : remoteLevel;
    const p2Level = isLocalP1 ? remoteLevel : localLevel;

    const updates: Record<string, unknown> = {
      result,
      player1_score: p1Score,
      player2_score: p2Score,
      player1_level: p1Level,
      player2_level: p2Level,
      ended_at: new Date().toISOString(),
      duration_sec: Math.round(durationSec)
    };

    // Only set winner_id if there's a winner (draw = null).
    // The Elo trigger only fires when winner_id transitions from NULL to non-NULL.
    if (winnerId != null) {
      updates.winner_id = winnerId;
    }

    const { error } = await supabase
      .from("matches")
      .update(updates)
      .eq("id", matchId);
    return error == null;
  } catch {
    return false;
  }
}

// ── Bot match Elo update ─────────────────────────────────────────────────────

/**
 * Update a player's ranked Elo after a bot match.
 * Uses standard Elo formula (K=32) matching the DB trigger logic.
 * Also increments ranked_wins/losses/games and updates peak rating.
 */
export async function updateBotMatchRating(
  playerId: string,
  localElo: number,
  opponentElo: number,
  won: boolean
): Promise<number | null> {
  try {
    const K = 32;
    const expected = 1 / (1 + Math.pow(10, (opponentElo - localElo) / 400));
    const score = won ? 1 : 0;
    const delta = Math.round(K * (score - expected));
    const newElo = Math.max(0, localElo + delta);

    const supabase = getSupabase();
    const updates: Record<string, unknown> = {
      versus_elo: newElo,
    };

    // Fetch current stats to increment
    const { data: profile } = await supabase
      .from("profiles")
      .select("ranked_wins, ranked_losses, ranked_games, ranked_peak_rating")
      .eq("id", playerId)
      .single();

    if (profile != null) {
      const rw = (profile.ranked_wins ?? 0) + (won ? 1 : 0);
      const rl = (profile.ranked_losses ?? 0) + (won ? 0 : 1);
      const rg = (profile.ranked_games ?? 0) + 1;
      const peak = Math.max(profile.ranked_peak_rating ?? 0, newElo);
      updates.ranked_wins = rw;
      updates.ranked_losses = rl;
      updates.ranked_games = rg;
      updates.ranked_peak_rating = peak;
    }

    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", playerId);

    if (error != null) return null;
    return newElo;
  } catch {
    return null;
  }
}

// ── Match events (audit) ────────────────────────────────────────────────────

export async function insertMatchEvent(
  matchId: string,
  playerId: string,
  seq: number,
  eventType: string,
  payload: unknown
): Promise<boolean> {
  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("match_events")
      .insert({
        match_id: matchId,
        player_id: playerId,
        seq,
        event_type: eventType,
        payload
      });
    return error == null;
  } catch {
    return false;
  }
}

// ── Match history ───────────────────────────────────────────────────────────

export interface MatchHistoryEntry {
  matchId: string;
  opponentName: string;
  opponentElo: number;
  won: boolean;
  result: MatchResult;
  myScore: number;
  opponentScore: number;
  myLevel: number;
  opponentLevel: number;
  durationSec: number;
  startedAt: number;
}

// ── Match validation ───────────────────────────────────────────────────────

export async function submitMatchSummary(summary: MatchSummary): Promise<boolean> {
  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("match_summaries")
      .insert({
        match_id: summary.matchId,
        player_id: summary.playerId,
        winner_id: summary.winnerId,
        result: summary.result,
        score: summary.score,
        level: summary.level,
        cash_balance: summary.cashBalance,
        traps_sent: summary.trapsSent,
        duration_sec: Math.round(summary.durationSec)
      });
    return error == null;
  } catch {
    return false;
  }
}

// ── Match history ───────────────────────────────────────────────────────────

export async function fetchMatchHistory(
  userId: string,
  limit = 20
): Promise<MatchHistoryEntry[]> {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("matches")
      .select(`
        id, player1_id, player2_id, winner_id, result,
        player1_score, player2_score, player1_level, player2_level,
        duration_sec, started_at
      `)
      .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
      .not("winner_id", "is", null)
      .order("started_at", { ascending: false })
      .limit(limit);
    if (error || data == null) return [];

    const opponentIds = data.map((row) =>
      row.player1_id === userId ? row.player2_id : row.player1_id
    );
    const uniqueIds = [...new Set(opponentIds)];

    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, display_name, versus_elo")
      .in("id", uniqueIds);

    const profileMap = new Map<string, { display_name: string; versus_elo: number }>();
    for (const p of profiles ?? []) {
      profileMap.set(p.id, p);
    }

    return data.map((row) => {
      const isPlayer1 = row.player1_id === userId;
      const opponentId = isPlayer1 ? row.player2_id : row.player1_id;
      const opp = profileMap.get(opponentId);
      return {
        matchId: row.id,
        opponentName: opp?.display_name ?? "PLAYER",
        opponentElo: opp?.versus_elo ?? 0,
        won: row.winner_id === userId,
        result: row.result as MatchResult,
        myScore: isPlayer1 ? row.player1_score : row.player2_score,
        opponentScore: isPlayer1 ? row.player2_score : row.player1_score,
        myLevel: isPlayer1 ? row.player1_level : row.player2_level,
        opponentLevel: isPlayer1 ? row.player2_level : row.player1_level,
        durationSec: row.duration_sec ?? 0,
        startedAt: new Date(row.started_at).getTime()
      };
    });
  } catch {
    return [];
  }
}
